import get from 'lodash/get';
import { defineStore } from 'pinia';

import {
  ApiChoiceMapper,
  ApiChoiceSource,
} from '@/features/ChoicesLists/Api/types';
import type {
  ApiChoice,
  ApiChoiceValue,
} from '@/features/ChoicesLists/Api/types';
import type { ChoicesApiHttpService } from '@/services/api/services/choicesApi';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';

interface State {
  data: {
    [key: string]: ApiChoiceValue[];
  };
}

export const useApiChoicesStore = defineStore('ChoicesListApiStore', {
  state: (): State => ({
    data: {},
  }),

  actions: {
    async loadChoice(choice: ApiChoice) {
      const choiceApi = useApi().choicesApi;

      const apiMethod: keyof ChoicesApiHttpService = ApiChoiceSource[
        choice.type
      ] as keyof ChoicesApiHttpService;
      const resultMapper: string = ApiChoiceMapper[choice.type];

      if (typeof choiceApi[apiMethod] !== 'function') {
        throw new VglError(VGL_ERROR_CODE.CHOICES_API_MISSING_ENDPOINT, {
          additionalErrorMessage: `missing ${apiMethod} endpoint in ChoicesApiHttpService`,
        });
      }

      try {
        const [, request] = choiceApi[apiMethod](...choice.requestParameters);

        const response = (await request) ?? [];

        const [labelKey, valueKey] = resultMapper.split('::');

        const choiceValues: ApiChoiceValue[] = [];

        if (Array.isArray(response)) {
          response.forEach((res) => {
            const label = get(res, labelKey);
            const value = get(res, valueKey);
            const { type } = res;

            if (label === undefined || value === undefined) {
              throw new VglError(
                VGL_ERROR_CODE.CHOICES_API_WRONG_API_RESPONSE,
                {
                  additionalErrorMessage: `missing id or value in response item -> Choice: ${JSON.stringify(
                    choice,
                  )} Response: ${JSON.stringify(response)}`,
                },
              );
            }

            choiceValues.push({ label, value, type });
          });
        } else {
          throw new VglError(VGL_ERROR_CODE.CHOICES_API_WRONG_API_RESPONSE, {
            additionalErrorMessage: `expecting choice array, got flat type ->Choice: ${JSON.stringify(
              choice,
            )} Response: ${JSON.stringify(response)}`,
          });
        }

        this.data[choice.id] = choiceValues;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.CHOICES_API_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      }
    },
  },
});
